/**
 * FluxNodeMonitor Page
 *
 * Main page for monitoring FluxNodes across multiple wallet addresses.
 * Displays comprehensive node statistics, rewards tracking, real APY,
 * parallel assets, and network information.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { FluxNodeDashboard } from '../components/fluxnode/FluxNodeDashboard';
import { FluxNodeProfitCalculator } from '../components/fluxnode/FluxNodeProfitCalculator';
import { HostingRig } from '../components/fluxnode/HostingRig';
import { BlockchainHealthMetrics } from '../components/fluxnode/BlockchainHealthMetrics';
import { FluxSupplyBreakdown } from '../components/fluxnode/FluxSupplyBreakdown';
import {
  useFluxNodes,
  useFluxPrice,
  useExtendedNetworkStats,
  useParallelAssets,
  useRewardPayouts,
  useRealNodeRewards,
  useWalletBalances,
  useDatavexHostedIps,
  useDatavexExpirations
} from '../hooks/useFluxNodes';
import { useFluxNodeWallets } from '../hooks/useMonitorWallets';
import { useToast } from '../components/Toast';
import { fluxNodeAPI, type FluxDeployedAppsSummary } from '../services/fluxNodeApi';
import { FluxNodeAlertSettings } from '../components/fluxnode/FluxNodeAlertSettings';
import { FluxNodeActiveAlerts } from '../components/fluxnode/FluxNodeActiveAlerts';
import { useFluxNodeActiveAlerts } from '../hooks/useFluxNodeActiveAlerts';
import type { UserMode } from '../hooks/useUserMode';

// Collateral tracking info per node (keyed by txhash:outidx)
export interface NodePurchaseInfo {
  purchasePrice: number; // Average price per FLUX at purchase
  purchaseDate?: string; // Optional ISO date string
  trackingStartDate?: string; // ISO date for when to start counting rewards (overrides PNR uptime)
}

export type NodePurchaseTracking = Record<string, NodePurchaseInfo>;

// Node aliases - keyed by nodeId (txhash:outidx)
export type NodeAliases = Record<string, string>;

interface FluxNodeMonitorProps {
  userMode?: UserMode;
}

export function FluxNodeMonitor({ userMode = 'simple' }: FluxNodeMonitorProps) {
  const { t } = useTranslation();

  // Valid tab names for URL hash
  const validTabs = ['monitoring', 'apps', 'hosting', 'calculator'] as const;
  type TabType = typeof validTabs[number];

  // Get initial tab from URL hash or default to 'monitoring'
  const getTabFromHash = (): TabType => {
    const hash = window.location.hash.replace('#', '');
    if (validTabs.includes(hash as TabType)) {
      // Don't allow calculator tab in simple mode
      if (hash === 'calculator' && userMode === 'simple') {
        return 'monitoring';
      }
      return hash as TabType;
    }
    return 'monitoring';
  };

  // Tab state - default to monitoring, only allow calculator in expert mode
  const [activeTab, setActiveTab] = useState<TabType>(getTabFromHash);

  // Update URL hash when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handleHashChange = () => {
      const newTab = getTabFromHash();
      setActiveTab(newTab);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [userMode]);

  // Reset to monitoring tab if user switches from expert to simple mode
  useEffect(() => {
    if (userMode === 'simple' && activeTab === 'calculator') {
      handleTabChange('monitoring');
    }
  }, [userMode, activeTab]);

  // Wallet management with Supabase sync
  const {
    wallets,
    setWallets,
    addWallet: addWalletToStore,
    removeWallet: removeWalletFromStore,
    purchaseTracking: nodePurchaseTracking = {},
    setPurchaseTracking: setNodePurchaseTracking,
    appsOwner: savedAppsOwner,
    setAppsOwner: saveAppsOwner,
    nodeAliases = {},
    setNodeAliases,
    isSyncing,
  } = useFluxNodeWallets();

  const [newWalletInput, setNewWalletInput] = useState('');
  const [showAddWallet, setShowAddWallet] = useState(false);
  const toast = useToast();

  // Deployed apps search state
  const [ownerSearchInput, setOwnerSearchInput] = useState('');
  const [deployedApps, setDeployedApps] = useState<FluxDeployedAppsSummary | null>(null);
  const [isLoadingApps, setIsLoadingApps] = useState(false);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [exampleOwners, setExampleOwners] = useState<{ owner: string; apps: { name: string; instances: number }[] }[]>([]);
  const [isLoadingExamples, setIsLoadingExamples] = useState(false);

  // Alert settings state
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [showActiveAlerts, setShowActiveAlerts] = useState(false);

  // Active alerts hook
  const { hasActiveAlerts, hasOfflineNodes, activeAlerts, offlineNodes } = useFluxNodeActiveAlerts();

  // Load saved apps owner on mount
  useEffect(() => {
    if (savedAppsOwner) {
      setOwnerSearchInput(savedAppsOwner);
    }
  }, [savedAppsOwner]);

  // Auto-search when switching to apps tab if saved owner exists
  useEffect(() => {
    if (activeTab === 'apps' && savedAppsOwner && !deployedApps && !isLoadingApps) {
      handleSearchDeployedApps(savedAppsOwner);
    }
  }, [activeTab, savedAppsOwner]);

  // Handle clearing saved apps owner
  const handleClearAppsOwner = () => {
    setOwnerSearchInput('');
    setDeployedApps(null);
    setAppsError(null);
    localStorage.removeItem('fluxnode_apps_owner');
    if (saveAppsOwner) {
      saveAppsOwner('');
    }
    toast(t('fluxNode.toasts.addressCleared', 'Address cleared'), 'success', 2000);
  };

  // Handle updating node purchase price
  const handleUpdateNodePurchasePrice = (nodeId: string, purchasePrice: number, purchaseDate?: string) => {
    if (setNodePurchaseTracking) {
      const existing = nodePurchaseTracking?.[nodeId];
      setNodePurchaseTracking({
        ...nodePurchaseTracking,
        [nodeId]: {
          purchasePrice,
          purchaseDate,
          // Preserve existing tracking start date if any
          trackingStartDate: existing?.trackingStartDate
        }
      });
    }
    toast(t('fluxNode.toasts.purchasePriceSaved'), 'success', 2000);
  };

  // Handle updating node tracking start date
  const handleUpdateTrackingStartDate = (nodeId: string, startDate: string | null) => {
    if (setNodePurchaseTracking) {
      const existing = nodePurchaseTracking?.[nodeId];
      if (existing) {
        setNodePurchaseTracking({
          ...nodePurchaseTracking,
          [nodeId]: {
            ...existing,
            trackingStartDate: startDate || undefined
          }
        });
        toast(
          startDate
            ? t('fluxNode.toasts.trackingDateSaved', 'Tracking start date saved')
            : t('fluxNode.toasts.trackingDateReset', 'Tracking date reset to PNR uptime'),
          'success',
          2000
        );
      }
    }
  };

  // Handle removing node purchase tracking
  const handleRemoveNodeTracking = (nodeId: string) => {
    if (setNodePurchaseTracking) {
      const updated = { ...nodePurchaseTracking };
      delete updated[nodeId];
      setNodePurchaseTracking(updated);
    }
  };

  // Handle updating node alias
  const handleUpdateNodeAlias = (nodeId: string, alias: string) => {
    if (setNodeAliases) {
      if (alias.trim()) {
        setNodeAliases({
          ...nodeAliases,
          [nodeId]: alias.trim()
        });
        toast(t('fluxNode.toasts.aliasSaved', 'Alias saved'), 'success', 2000);
      } else {
        // Remove alias if empty
        const updated = { ...nodeAliases };
        delete updated[nodeId];
        setNodeAliases(updated);
        toast(t('fluxNode.toasts.aliasRemoved', 'Alias removed'), 'info', 2000);
      }
    }
  };

  // Handle removing node alias
  const handleRemoveNodeAlias = (nodeId: string) => {
    if (setNodeAliases) {
      const updated = { ...nodeAliases };
      delete updated[nodeId];
      setNodeAliases(updated);
    }
  };

  // Handle searching for deployed apps by owner
  const handleSearchDeployedApps = async (addressOverride?: string | React.MouseEvent) => {
    // Handle case where event is passed instead of string (from onClick)
    const address = typeof addressOverride === 'string' ? addressOverride : ownerSearchInput;
    const trimmedAddress = (address || '').trim();

    if (!trimmedAddress) {
      toast(t('fluxNode.toasts.pleaseEnterOwnerAddress'), 'warning', 3000);
      return;
    }

    // Update input if using override string
    if (typeof addressOverride === 'string') {
      setOwnerSearchInput(addressOverride);
    }

    setIsLoadingApps(true);
    setAppsError(null);
    setDeployedApps(null);

    try {
      const result = await fluxNodeAPI.getDeployedAppsByOwner(trimmedAddress);
      if (result) {
        setDeployedApps(result);
        // Save owner to localStorage for persistence
        localStorage.setItem('fluxnode_apps_owner', trimmedAddress);
        // IMPORTANT: Sync to Supabase for cron job alerts
        if (saveAppsOwner) {
          saveAppsOwner(trimmedAddress);
        }
        if (result.totalApps > 0) {
          toast(`${result.totalApps} ${t('fluxNode.toasts.appsFound')}`, 'success', 3000);
        } else {
          toast(t('fluxNode.toasts.noAppsFoundForAddress'), 'info', 3000);
        }
        // Track apps search with result count
      } else {
        setAppsError(t('fluxNode.toasts.searchError'));
        toast(t('fluxNode.toasts.searchError'), 'error', 3000);
      }
    } catch (error) {
      console.error('Error searching deployed apps:', error);
      setAppsError(error instanceof Error ? error.message : t('fluxNode.monitoring.unknownError'));
      toast(t('fluxNode.toasts.searchError'), 'error', 3000);
    } finally {
      setIsLoadingApps(false);
    }
  };

  // Handle finding example wallet owners with apps
  const handleFindExamples = async (searchTerm: string = '') => {
    setIsLoadingExamples(true);
    setAppsError(null);

    try {
      toast(`${t('fluxNode.toasts.searchingExamples')}${searchTerm ? ` (${searchTerm})` : ''}...`, 'info', 2000);
      const results = await fluxNodeAPI.findAppOwners(searchTerm, 20);

      if (results.length > 0) {
        setExampleOwners(results);
        toast(`${results.length} ${t('fluxNode.toasts.walletsFoundWithApps')}`, 'success', 3000);
        // Track example finder usage
      } else {
        toast(t('fluxNode.toasts.noWalletsFound'), 'warning', 3000);
      }
    } catch (error) {
      console.error('Error finding examples:', error);
      toast(t('fluxNode.toasts.searchError'), 'error', 3000);
    } finally {
      setIsLoadingExamples(false);
    }
  };

  // Fetch data
  const { nodes, isLoading, error, refetch } = useFluxNodes(wallets);
  const { data: fluxPrice = 0 } = useFluxPrice();

  // New hooks for extended data
  const { data: extendedStats } = useExtendedNetworkStats();
  const { data: parallelAssets } = useParallelAssets(wallets);
  const { data: rewardPayouts } = useRewardPayouts(wallets);
  const { data: walletBalances } = useWalletBalances(wallets);
  const { data: datavexHostedIps } = useDatavexHostedIps();
  const { data: datavexExpirations } = useDatavexExpirations();

  // Calculate real rewards
  const realRewards = useRealNodeRewards(nodes, extendedStats);

  // Handle adding wallet
  const handleAddWallet = () => {
    const trimmedAddress = newWalletInput.trim();

    if (!trimmedAddress) {
      toast(t('fluxNode.toasts.pleaseEnterWalletAddress'), 'warning', 3000);
      return;
    }

    // Basic validation (Flux addresses start with 't1' or 't3' for multisig SSP Wallet)
    if (!trimmedAddress.startsWith('t1') && !trimmedAddress.startsWith('t3')) {
      toast(t('fluxNode.toasts.invalidWalletFormat'), 'error', 4000);
      return;
    }

    if (wallets.includes(trimmedAddress)) {
      toast(t('fluxNode.toasts.walletAlreadyAdded'), 'warning', 3000);
      return;
    }

    setWallets([...wallets, trimmedAddress]);
    setNewWalletInput('');
    setShowAddWallet(false);
    toast(t('fluxNode.toasts.walletAddedSuccess'), 'success', 3000);

    // Track wallet addition (only prefix for privacy)
  };

  // Handle removing wallet
  const handleRemoveWallet = (address: string) => {
    if (confirm(`${t('fluxNode.monitoring.removeWalletConfirm')} ${address.substring(0, 10)}...?`)) {
      setWallets(wallets.filter(w => w !== address));
      toast(t('fluxNode.toasts.walletRemoved'), 'info', 3000);

      // Track wallet removal
    }
  };

  return (
    <div style={{ paddingBottom: '40px' }}>
      {/* Header with Tab Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icons.Flux size={32} />
            {t('fluxNode.title')}
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
            {t('fluxNode.subtitle')}
          </p>
          <a
            href="https://fluxme.io"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '8px',
              padding: '6px 14px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#2563eb',
              background: 'rgba(37, 99, 235, 0.08)',
              border: '1px solid rgba(37, 99, 235, 0.2)',
              borderRadius: '8px',
              textDecoration: 'none',
              transition: 'all 0.2s',
            }}
          >
            <Icons.ExternalLink size={14} />
            Everything you need to know about $FLUX
          </a>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '4px',
          background: 'var(--border)',
          padding: '4px',
          borderRadius: '12px',
          width: 'fit-content'
        }}>
        <button
          onClick={() => handleTabChange('monitoring')}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            background: activeTab === 'monitoring' ? 'var(--accent)' : 'transparent',
            color: activeTab === 'monitoring' ? 'white' : 'var(--text)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <Icons.Flux size={18} />
          {t('fluxNode.tabs.monitoring')}
        </button>
        <button
          onClick={() => handleTabChange('apps')}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            background: activeTab === 'apps' ? 'var(--accent)' : 'transparent',
            color: activeTab === 'apps' ? 'white' : 'var(--text)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <Icons.Package size={18} />
          {t('fluxNode.tabs.applications')}
        </button>
        <button
          onClick={() => handleTabChange('hosting')}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            background: activeTab === 'hosting' ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' : 'transparent',
            color: activeTab === 'hosting' ? 'white' : 'var(--text)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <Icons.Cloud size={18} />
          {t('fluxNode.tabs.hosting', 'Hosting Rig')}
        </button>
        {userMode === 'expert' && (
          <button
            onClick={() => handleTabChange('calculator')}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              background: activeTab === 'calculator' ? 'var(--accent)' : 'transparent',
              color: activeTab === 'calculator' ? 'white' : 'var(--text)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <Icons.Calculator size={18} />
            {t('fluxNode.tabs.calculator')}
          </button>
        )}
        </div>
      </div>

      {/* Tab Content: Calculator - Expert mode only */}
      {userMode === 'expert' && activeTab === 'calculator' && (
        <FluxNodeProfitCalculator
          fluxPrice={fluxPrice}
          realRewards={realRewards}
          nodes={nodes}
          nodePurchaseTracking={nodePurchaseTracking}
        />
      )}

      {/* Tab Content: Applications */}
      {activeTab === 'apps' && (
        <div className="card">
          {/* Search Form */}
          <div style={{ marginBottom: '20px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Icons.Search size={20} />
              {t('fluxNode.apps.searchByOwner')}
            </h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={ownerSearchInput}
                onChange={(e) => setOwnerSearchInput(e.target.value)}
                placeholder={t('fluxNode.apps.enterOwnerAddress')}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  fontSize: '14px',
                  background: 'var(--border)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '8px',
                  fontFamily: 'monospace'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchDeployedApps();
                  }
                }}
              />
              <button
                onClick={handleSearchDeployedApps}
                disabled={isLoadingApps}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  background: isLoadingApps ? 'var(--muted)' : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLoadingApps ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {isLoadingApps ? (
                  <>
                    <Icons.RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    {t('fluxNode.apps.searching')}
                  </>
                ) : (
                  <>
                    <Icons.Search size={16} />
                    {t('fluxNode.apps.search')}
                  </>
                )}
              </button>
              {/* Delete button - only show when there's a saved address */}
              {savedAppsOwner && (
                <button
                  onClick={handleClearAppsOwner}
                  title={t('fluxNode.apps.clearSavedAddress', 'Clear saved address')}
                  style={{
                    padding: '12px 14px',
                    fontSize: '14px',
                    background: 'var(--border)',
                    color: 'var(--error)',
                    border: '1px solid var(--error)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--error)';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--border)';
                    e.currentTarget.style.color = 'var(--error)';
                  }}
                >
                  <Icons.Trash2 size={16} />
                </button>
              )}
            </div>
            <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {t('fluxNode.apps.searchHelp')}
              {savedAppsOwner && (
                <span style={{
                  padding: '2px 8px',
                  background: 'rgba(16, 185, 129, 0.15)',
                  color: '#10b981',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 500
                }}>
                  ✓ {t('fluxNode.apps.savedAddress', 'Saved address')}
                </span>
              )}
            </div>
          </div>

          {/* Find Examples Section */}
          <div style={{
            padding: '16px',
            background: 'var(--border)',
            borderRadius: '10px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: 'var(--text)', fontWeight: 500, marginRight: '8px' }}>
                {t('fluxNode.apps.findExamples')}
              </span>
              <button
                onClick={() => handleFindExamples('')}
                disabled={isLoadingExamples}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '6px',
                  cursor: isLoadingExamples ? 'not-allowed' : 'pointer',
                  opacity: isLoadingExamples ? 0.5 : 1
                }}
              >
                {t('fluxNode.apps.all')}
              </button>
              <button
                onClick={() => handleFindExamples('wordpress')}
                disabled={isLoadingExamples}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '6px',
                  cursor: isLoadingExamples ? 'not-allowed' : 'pointer',
                  opacity: isLoadingExamples ? 0.5 : 1
                }}
              >
                WordPress
              </button>
              <button
                onClick={() => handleFindExamples('presearch')}
                disabled={isLoadingExamples}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '6px',
                  cursor: isLoadingExamples ? 'not-allowed' : 'pointer',
                  opacity: isLoadingExamples ? 0.5 : 1
                }}
              >
                Presearch
              </button>
              <button
                onClick={() => handleFindExamples('kadena')}
                disabled={isLoadingExamples}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '6px',
                  cursor: isLoadingExamples ? 'not-allowed' : 'pointer',
                  opacity: isLoadingExamples ? 0.5 : 1
                }}
              >
                Kadena
              </button>
              <button
                onClick={() => handleFindExamples('firo')}
                disabled={isLoadingExamples}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '6px',
                  cursor: isLoadingExamples ? 'not-allowed' : 'pointer',
                  opacity: isLoadingExamples ? 0.5 : 1
                }}
              >
                Firo
              </button>
              <button
                onClick={() => handleFindExamples('kaspa')}
                disabled={isLoadingExamples}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '6px',
                  cursor: isLoadingExamples ? 'not-allowed' : 'pointer',
                  opacity: isLoadingExamples ? 0.5 : 1
                }}
              >
                Kaspa
              </button>
              <button
                onClick={() => handleFindExamples('dash')}
                disabled={isLoadingExamples}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '6px',
                  cursor: isLoadingExamples ? 'not-allowed' : 'pointer',
                  opacity: isLoadingExamples ? 0.5 : 1
                }}
              >
                Dash
              </button>
              <button
                onClick={() => handleFindExamples('ravencoin')}
                disabled={isLoadingExamples}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '6px',
                  cursor: isLoadingExamples ? 'not-allowed' : 'pointer',
                  opacity: isLoadingExamples ? 0.5 : 1
                }}
              >
                Ravencoin
              </button>
              <button
                onClick={() => handleFindExamples('folding')}
                disabled={isLoadingExamples}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '6px',
                  cursor: isLoadingExamples ? 'not-allowed' : 'pointer',
                  opacity: isLoadingExamples ? 0.5 : 1
                }}
              >
                Folding@Home
              </button>
              <button
                onClick={() => handleFindExamples('minecraft')}
                disabled={isLoadingExamples}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--card-bg)',
                  borderRadius: '6px',
                  cursor: isLoadingExamples ? 'not-allowed' : 'pointer',
                  opacity: isLoadingExamples ? 0.5 : 1
                }}
              >
                Minecraft
              </button>
              {isLoadingExamples && (
                <span style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Icons.RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  {t('fluxNode.apps.searchingInProgress')}
                </span>
              )}
            </div>

            {/* Example Owners Results */}
            {exampleOwners.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                  {t('fluxNode.apps.walletsWithApps')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                  {exampleOwners.slice(0, 10).map((owner, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSearchDeployedApps(owner.owner)}
                      style={{
                        padding: '10px 14px',
                        fontSize: '13px',
                        background: 'var(--card-bg)',
                        color: 'var(--text)',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s'
                      }}
                    >
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {owner.owner.substring(0, 20)}...{owner.owner.substring(owner.owner.length - 8)}
                      </span>
                      <span style={{
                        padding: '4px 10px',
                        background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600
                      }}>
                        {owner.apps.length} {owner.apps.length > 1 ? t('fluxNode.apps.apps') : t('fluxNode.apps.app')}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Error State */}
          {appsError && (
            <div style={{
              padding: '12px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#ef4444'
            }}>
              <Icons.AlertCircle size={18} />
              {appsError}
            </div>
          )}

          {/* Results */}
          {deployedApps && (
            <div>
              {/* Owner Header Card */}
              <div style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                borderRadius: '16px',
                marginBottom: '20px',
                color: 'white'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('fluxNode.apps.ownerAddress')}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: 500, wordBreak: 'break-all' }}>
                      {deployedApps.owner}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(deployedApps.owner);
                        toast(t('fluxNode.apps.addressCopied'), 'success', 2000);
                      }}
                      style={{
                        padding: '8px 12px',
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px'
                      }}
                    >
                      <Icons.Copy size={14} />
                      {t('fluxNode.apps.copy')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Summary Stats Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '12px',
                marginBottom: '24px'
              }}>
                {/* Apps Count */}
                <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                    <div style={{ padding: '10px', background: 'rgba(139, 92, 246, 0.15)', borderRadius: '12px' }}>
                      <Icons.Package size={24} style={{ color: '#8b5cf6' }} />
                    </div>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#8b5cf6' }}>
                    {deployedApps.totalApps}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{t('fluxNode.apps.applications')}</div>
                </div>

                {/* Instances */}
                <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                    <div style={{ padding: '10px', background: 'rgba(16, 185, 129, 0.15)', borderRadius: '12px' }}>
                      <Icons.Server size={24} style={{ color: '#10b981' }} />
                    </div>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981' }}>
                    {deployedApps.totalInstances}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{t('fluxNode.apps.instances')}</div>
                </div>

                {/* CPU */}
                <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                    <div style={{ padding: '10px', background: 'rgba(245, 158, 11, 0.15)', borderRadius: '12px' }}>
                      <Icons.Cpu size={24} style={{ color: '#f59e0b' }} />
                    </div>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#f59e0b' }}>
                    {deployedApps.totalCpu}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{t('fluxNode.apps.cpuCores')}</div>
                </div>

                {/* RAM */}
                <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                    <div style={{ padding: '10px', background: 'rgba(59, 130, 246, 0.15)', borderRadius: '12px' }}>
                      <Icons.Activity size={24} style={{ color: '#3b82f6' }} />
                    </div>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#3b82f6' }}>
                    {(deployedApps.totalRam / 1024).toFixed(1)}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{t('fluxNode.apps.ramGb')}</div>
                </div>

                {/* HDD */}
                <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                    <div style={{ padding: '10px', background: 'rgba(236, 72, 153, 0.15)', borderRadius: '12px' }}>
                      <Icons.HardDrive size={24} style={{ color: '#ec4899' }} />
                    </div>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#ec4899' }}>
                    {deployedApps.totalHdd}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{t('fluxNode.apps.storageGb')}</div>
                </div>

                {/* Estimated Cost */}
                <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                    <div style={{ padding: '10px', background: 'rgba(34, 197, 94, 0.15)', borderRadius: '12px' }}>
                      <Icons.DollarSign size={24} style={{ color: '#22c55e' }} />
                    </div>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#22c55e' }}>
                    ~{(() => {
                      const cost = fluxNodeAPI.calculateAppCost(deployedApps.totalCpu, deployedApps.totalRam, deployedApps.totalHdd, 1);
                      return cost.monthlyCost.toFixed(0);
                    })()}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{t('fluxNode.apps.fluxPerMonth')}</div>
                </div>
              </div>

              {/* Apps List Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icons.Grid size={18} />
                  {t('fluxNode.apps.deployedApplications')}
                </h3>
                <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
                  {deployedApps.apps.length} {deployedApps.apps.length > 1 ? t('fluxNode.apps.apps') : t('fluxNode.apps.app')}
                </span>
              </div>

              {/* Apps List */}
              {deployedApps.apps.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
                  gap: '16px'
                }}>
                  {deployedApps.apps.map((app, index) => {
                    // Calculate app resources
                    const appCpu = app.compose?.reduce((sum, c) => sum + (c.cpu || 0), 0) || 0;
                    const appRam = app.compose?.reduce((sum, c) => sum + (c.ram || 0), 0) || 0;
                    const appHdd = app.compose?.reduce((sum, c) => sum + (c.hdd || 0), 0) || 0;
                    const appCost = fluxNodeAPI.calculateAppCost(appCpu, appRam, appHdd, app.instances || 1);

                    return (
                      <div
                        key={app.hash || index}
                        className="card"
                        style={{
                          padding: '0',
                          overflow: 'hidden',
                          border: '1px solid var(--border)'
                        }}
                      >
                        {/* App Header */}
                        <div style={{
                          padding: '14px 16px',
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(99, 102, 241, 0.08))',
                          borderBottom: '1px solid var(--border)'
                        }}>
                          {/* Top row: Icon, Name, Status */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
                            <div style={{
                              width: '42px',
                              height: '42px',
                              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Icons.Package size={20} style={{ color: 'white' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {app.name || t('fluxNode.apps.unnamedApp')}
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>v{app.version}</span>
                                <span>•</span>
                                <span>#{app.height.toLocaleString()}</span>
                              </div>
                            </div>
                            <div style={{
                              padding: '4px 10px',
                              background: app.instances > 0 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                              color: app.instances > 0 ? '#10b981' : '#ef4444',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              flexShrink: 0
                            }}>
                              <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: app.instances > 0 ? '#10b981' : '#ef4444'
                              }} />
                              {app.instances}
                            </div>
                          </div>

                          {/* Description */}
                          {app.description && (
                            <p style={{
                              fontSize: '12px',
                              color: 'var(--muted)',
                              margin: '0 0 10px 0',
                              lineHeight: 1.4,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {app.description}
                            </p>
                          )}

                          {/* Cost and Visit Button */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                              ~{appCost.monthlyCost.toFixed(1)} FLUX/mois
                            </div>
                            {app.instances > 0 && app.name && (
                              <a
                                href={`https://${app.name.toLowerCase()}.app.runonflux.io`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  padding: '6px 12px',
                                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                                  color: 'white',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  textDecoration: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <Icons.ExternalLink size={12} />
                                {t('fluxNode.apps.visit')}
                              </a>
                            )}
                          </div>
                        </div>

                        {/* App Body */}
                        <div style={{ padding: '14px 16px' }}>
                          {/* Resource Bars */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '12px',
                            marginBottom: '14px'
                          }}>
                            {/* CPU */}
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Icons.Cpu size={12} /> {t('fluxNode.apps.cpu')}
                                </span>
                                <span style={{ fontSize: '12px', fontWeight: 600 }}>{appCpu} cores</span>
                              </div>
                              <div style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{
                                  height: '100%',
                                  width: `${Math.min((appCpu / 8) * 100, 100)}%`,
                                  background: 'linear-gradient(90deg, #f59e0b, #f97316)',
                                  borderRadius: '3px'
                                }} />
                              </div>
                            </div>

                            {/* RAM */}
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Icons.Activity size={12} /> {t('fluxNode.apps.ram')}
                                </span>
                                <span style={{ fontSize: '12px', fontWeight: 600 }}>{(appRam / 1024).toFixed(1)} GB</span>
                              </div>
                              <div style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{
                                  height: '100%',
                                  width: `${Math.min((appRam / 32768) * 100, 100)}%`,
                                  background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                                  borderRadius: '3px'
                                }} />
                              </div>
                            </div>

                            {/* Storage */}
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Icons.HardDrive size={12} /> {t('fluxNode.apps.storage')}
                                </span>
                                <span style={{ fontSize: '12px', fontWeight: 600 }}>{appHdd} GB</span>
                              </div>
                              <div style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{
                                  height: '100%',
                                  width: `${Math.min((appHdd / 500) * 100, 100)}%`,
                                  background: 'linear-gradient(90deg, #ec4899, #f43f5e)',
                                  borderRadius: '3px'
                                }} />
                              </div>
                            </div>
                          </div>

                          {/* Components - Compact view */}
                          {app.compose && app.compose.length > 0 && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{
                                fontSize: '11px',
                                color: 'var(--muted)',
                                marginBottom: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <Icons.Layers size={12} />
                                {app.compose.length} {app.compose.length > 1 ? t('fluxNode.apps.components') : t('fluxNode.apps.component')}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {app.compose.map((comp, compIdx) => (
                                  <div
                                    key={compIdx}
                                    style={{
                                      padding: '6px 10px',
                                      background: 'var(--border)',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px'
                                    }}
                                  >
                                    <Icons.Box size={12} style={{ color: '#8b5cf6' }} />
                                    <span style={{ fontWeight: 600 }}>{comp.name}</span>
                                    {comp.repotag && (
                                      <span style={{ color: 'var(--muted)', fontSize: '10px' }}>
                                        🐳
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Footer with Hash and Expiry */}
                          <div style={{
                            paddingTop: '10px',
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '6px'
                          }}>
                            {app.hash && (
                              <div style={{
                                fontSize: '11px',
                                color: 'var(--muted)',
                                fontFamily: 'monospace',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <Icons.Hash size={12} />
                                {app.hash.substring(0, 16)}...{app.hash.substring(app.hash.length - 8)}
                              </div>
                            )}
                            <div style={{
                              fontSize: '11px',
                              color: 'var(--muted)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <Icons.Clock size={12} />
                              {t('fluxNode.apps.expiresBlock')}{app.expire.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="card" style={{
                  textAlign: 'center',
                  padding: '40px'
                }}>
                  <Icons.Package size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                  <p style={{ color: 'var(--muted)' }}>{t('fluxNode.apps.noAppsFound')}</p>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!deployedApps && !appsError && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: 'var(--muted)'
            }}>
              <Icons.Cloud size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p style={{ fontSize: '16px', marginBottom: '8px' }}>{t('fluxNode.apps.searchAppsOnFlux')}</p>
              <p style={{ fontSize: '13px' }}>
                {t('fluxNode.apps.enterOwnerOrUseExamples')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tab Content: Hosting Rig */}
      {activeTab === 'hosting' && (
        <HostingRig
          wallets={wallets}
          nodes={nodes}
          fluxPrice={fluxPrice}
          extendedStats={extendedStats}
          datavexHostedIps={datavexHostedIps}
          datavexExpirations={datavexExpirations}
        />
      )}

      {/* Tab Content: Monitoring */}
      {activeTab === 'monitoring' && (
        <>
      {/* Wallet Management Section */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600 }}>
            {t('fluxNode.monitoring.walletAddresses')} ({wallets.length})
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Active Alerts Button */}
            <button
              onClick={() => { setShowActiveAlerts(!showActiveAlerts); setShowAlertSettings(false); }}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                background: showActiveAlerts
                  ? '#ef4444'
                  : (hasActiveAlerts || hasOfflineNodes)
                    ? 'rgba(239, 68, 68, 0.15)'
                    : 'var(--border)',
                color: showActiveAlerts
                  ? 'white'
                  : (hasActiveAlerts || hasOfflineNodes)
                    ? '#ef4444'
                    : 'var(--text)',
                border: (hasActiveAlerts || hasOfflineNodes) && !showActiveAlerts
                  ? '1px solid rgba(239, 68, 68, 0.5)'
                  : 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
                position: 'relative'
              }}
              title={t('fluxNode.activeAlerts.title', 'Alertes Actives')}
            >
              <Icons.AlertTriangle size={16} />
              {t('fluxNode.activeAlerts.title', 'Alertes')}
              {(hasActiveAlerts || hasOfflineNodes) && (
                <span style={{
                  background: showActiveAlerts ? 'white' : '#ef4444',
                  color: showActiveAlerts ? '#ef4444' : 'white',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  minWidth: '18px',
                  textAlign: 'center'
                }}>
                  {activeAlerts.length + offlineNodes.length}
                </span>
              )}
            </button>
            {/* Alert Settings Button */}
            <button
              onClick={() => { setShowAlertSettings(!showAlertSettings); setShowActiveAlerts(false); }}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                background: showAlertSettings ? 'var(--primary)' : 'var(--border)',
                color: showAlertSettings ? 'white' : 'var(--text)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!showAlertSettings) {
                  e.currentTarget.style.background = 'var(--muted)';
                }
              }}
              onMouseLeave={(e) => {
                if (!showAlertSettings) {
                  e.currentTarget.style.background = 'var(--border)';
                }
              }}
              title={t('fluxNode.alerts.settings', 'Parametres')}
            >
              <Icons.Settings size={16} />
              {t('fluxNode.alerts.settings', 'Parametres')}
            </button>
            {/* Add Wallet Button */}
            <button
              onClick={() => setShowAddWallet(!showAddWallet)}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                background: 'var(--accent)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Icons.Plus size={16} />
              {t('fluxNode.monitoring.addWallet')}
            </button>
          </div>
        </div>

        {/* Add Wallet Form */}
        {showAddWallet && (
          <div
            style={{
              padding: '16px',
              background: 'var(--border)',
              borderRadius: '8px',
              marginBottom: '16px'
            }}
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={newWalletInput}
                onChange={(e) => setNewWalletInput(e.target.value)}
                placeholder={t('fluxNode.monitoring.enterWalletAddress')}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  fontSize: '14px',
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontFamily: 'monospace'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddWallet();
                  }
                }}
              />
              <button
                onClick={handleAddWallet}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                {t('fluxNode.monitoring.add')}
              </button>
              <button
                onClick={() => {
                  setShowAddWallet(false);
                  setNewWalletInput('');
                }}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                {t('fluxNode.monitoring.cancel')}
              </button>
            </div>
            <div style={{
              marginTop: '8px',
              fontSize: '12px',
              color: 'var(--muted)'
            }}>
              💡 {t('fluxNode.monitoring.example')}: t1... {t('fluxNode.monitoring.orMultisig')} t3...
            </div>
          </div>
        )}

        {/* Wallet List */}
        {wallets.length > 0 ? (
          <div style={{ display: 'grid', gap: '8px' }}>
            {wallets.map((wallet, index) => (
              <div
                key={wallet}
                style={{
                  padding: '12px 16px',
                  background: 'var(--border)',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, fontFamily: 'monospace' }}>
                    {wallet}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                    {t('fluxNode.monitoring.wallet')} #{index + 1}
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveWallet(wallet)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  {t('fluxNode.monitoring.remove')}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: 'var(--muted)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💼</div>
            <p>{t('fluxNode.monitoring.noWalletsYet')}</p>
            <p style={{ fontSize: '14px', marginTop: '8px' }}>
              {t('fluxNode.monitoring.addWalletToStart')}
            </p>
          </div>
        )}
      </div>

      {/* Active Alerts Panel */}
      {showActiveAlerts && (
        <div style={{ marginBottom: '30px' }}>
          <FluxNodeActiveAlerts onClose={() => setShowActiveAlerts(false)} />
        </div>
      )}

      {/* Alert Settings Panel */}
      {showAlertSettings && (
        <div style={{ marginBottom: '30px' }}>
          <FluxNodeAlertSettings onClose={() => setShowAlertSettings(false)} />
        </div>
      )}

      {/* Network Info Banner */}
      {fluxPrice > 0 && (
        <div
          style={{
            padding: '16px',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            borderRadius: '12px',
            marginBottom: '20px',
            color: 'white'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                {t('fluxNode.monitoring.fluxPrice')}
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700 }}>
                ${fluxPrice.toFixed(4)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                {t('fluxNode.monitoring.liveData')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                CoinGecko API
              </div>
            </div>
          </div>
        </div>
      )}

      {/* V2.K.2 — FLUX supply breakdown (circulating / remaining-to-mint
          + PoUW v2 emission note + foundation funding stream). Fed by
          CoinGecko, refreshes every 5 min. */}
      <FluxSupplyBreakdown />

      {/* Blockchain Health Metrics - Collapsible */}
      <BlockchainHealthMetrics extendedStats={extendedStats} />

      {/* Error State */}
      {error && (
        <div className="card" style={{
          padding: '20px',
          marginBottom: '20px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icons.AlertCircle size={24} style={{ color: '#ef4444' }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                {t('fluxNode.monitoring.errorLoadingNodes')}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--muted)' }}>
                {error instanceof Error ? error.message : t('fluxNode.monitoring.unknownError')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard */}
      {wallets.length > 0 && (
        <FluxNodeDashboard
          nodes={nodes}
          fluxPrice={fluxPrice}
          isLoading={isLoading}
          onRefresh={refetch}
          wallets={wallets}
          extendedStats={extendedStats}
          parallelAssets={parallelAssets}
          rewardPayouts={rewardPayouts}
          realRewards={realRewards}
          nodePurchaseTracking={nodePurchaseTracking}
          onUpdateNodePurchasePrice={handleUpdateNodePurchasePrice}
          onUpdateTrackingStartDate={handleUpdateTrackingStartDate}
          onRemoveNodeTracking={handleRemoveNodeTracking}
          walletBalances={walletBalances}
          nodeAliases={nodeAliases}
          onUpdateNodeAlias={handleUpdateNodeAlias}
          onRemoveNodeAlias={handleRemoveNodeAlias}
          datavexExpirations={datavexExpirations}
        />
      )}
        </>
      )}
    </div>
  );
}
